import PointIcon from "@/assets/images/point.svg";
import type { IDeposit } from "@/modules/deposit/common/types";
import { getDepositTitle } from "@/modules/deposit/helpers/depositHelpers";
import { CloudflareAsset } from "@/shared/common/enums";
import { Amount, CloudFlareImage } from "@/shared/components";
import { MoreIcon } from "@/shared/components/SvgIcons";
import { formatDate } from "@/shared/helpers";
import { useAppSelector, useMediaQuery } from "@/shared/hooks";
import { Divider } from "@ab.uitools/ui-library/components/Divider";
import { Image } from "@ab.uitools/ui-library/components/Image";
import { Text } from "@ab.uitools/ui-library/components/Text";
import type { MouseEvent } from "react";
import { useTranslation } from "react-i18next";

interface Props extends IDeposit {
  index: number;
  className?: string;
  hasMoreAction?: boolean;
  handleShowBalances: (i: number) => void;
}

export const DepositsItem = ({
  nominalInterestRate,
  balance,
  index,
  name,
  currency,
  className,
  calculatedInterestAmount,
  isReplenishable,
  isWithdrawable,
  isCapitalizable,
  isExtendable,
  maturityDate,
  hasMoreAction,
  handleShowBalances,
}: Props) => {
  const isDesktop = useMediaQuery();
  const { t } = useTranslation();
  const showBalances = useAppSelector((state) => state.dashboard.showBalances);

  const onShowBalance = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    handleShowBalances(index);
  };

  return (
    <div
      className={`full-width flexbox align-items--center pl-32 pr-32 widget-content-item ${className}`}>
      <div className="item-icon flexbox align-items--center justify-content--center">
        <div className="flexbox align-items--center justify-content--center">
          <CloudFlareImage path={CloudflareAsset.Loan}/>
        </div>
      </div>
      <div className="deposit-item-content flexbox flex-direction--column pl-16">
        {!!index && (
          <Divider color="light" type="primary" isHorizontal/>
        )}
        <div className="flexbox full-width pt-18 pb-18 deposit-item-body-wrapper">
          <div className="flexbox flex-direction--column deposit-item-body">
            <div className="full-width flexbox justify-content--between">
              <Text className="deposit-item-title pr-16 truncate" size="medium" weight="semibold">
                {name || t(getDepositTitle(isReplenishable, isWithdrawable))}
              </Text>
              <div
                onClick={onShowBalance}
                className="flexbox justify-content--center align-items--end flex-direction--column pr-6"
              >
                <Amount
                  className="deposit-item-amount"
                  currency={currency}
                  amount={balance}
                  isShow={showBalances[index]}
                />
              </div>
            </div>
            <div className="full-width flexbox justify-content--between">
              <div className="mt-4 deposit-item-rate-wrapper truncate-block">
                <Text className="mr-4" type="tertiary">
                  {nominalInterestRate}%
                </Text>
                <Image imagePath={PointIcon}/>
                <Text className="mr-4 ml-4 text-nowrap" type="tertiary">
                  {`${isDesktop ? `${t("deposits.maturityDate")} : ` : ""}${formatDate(maturityDate)}`}
                </Text>
                {isCapitalizable && (
                  <>
                    <Image imagePath={PointIcon}/>
                    <Text className="ml-4 mr-4" type="information-dark">
                      {t("deposits.capitalizable")}
                    </Text>
                  </>
                )}
                {isExtendable && (
                  <>
                    <Image imagePath={PointIcon}/>
                    <Text className="ml-4 mr-4" type="information-dark">
                      {t("deposits.extendable")}
                    </Text>
                  </>
                )}
              </div>
              <div
                onClick={onShowBalance}
                className="flexbox justify-content--center align-items--end flex-direction--column pr-6"
              >
                <Amount
                  type="tertiary"
                  currency={currency}
                  size="standard"
                  className="mt-4 deposit-item-interest-amount pl-8"
                  amount={calculatedInterestAmount}
                  isShow={showBalances[index]}
                />
              </div>
            </div>
          </div>
          {hasMoreAction && (
            <div className="cursor-pointer flexbox align-items--center">
              <MoreIcon type="secondary"/>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
